import { Divider, Typography } from '@mui/material';
import { useLayoutContext } from 'contexts/LayoutContext';
import { InvestmentRevaluationOption } from 'further-types/lp/investment';
import BasicSelect from 'further-ui/components/BasicSelect';
import FormField from 'further-ui/components/FormField';
import { Column } from 'further-ui/components/Layout';
import { investment as rules } from 'further-ui/validations';
import api from 'lib/trpcClient';
import { Controller, useFormContext } from 'react-hook-form';
import RevaluationFormWrapper from './RevaluationFormWrapper';

type RevalueInvestmentsProps = {
  onSuccess: () => void;
  onCancel: () => void;
};

const RevalueInvestments: React.FC<RevalueInvestmentsProps> = ({
  onSuccess,
  onCancel,
}) => {
  const { control, watch } = useFormContext<rules.CreateInvestmentFormValues>();
  const { selectedVehicleId } = useLayoutContext();
  const assetId = watch('assetId');
  const newRevaluations = watch('newRevaluations');
  const [holdingClassInvestmentTotals] =
    api.asset.getAssetHoldingClassInvestmentTotals.useSuspenseQuery({
      assetId,
      vehicleId: selectedVehicleId ?? '',
    });

  const holdingClasses = watch('holdingClasses');

  const unitPriceDifferentForHoldingClasses =
    !holdingClassInvestmentTotals.length
      ? false
      : holdingClasses.some((holdingClass) => {
          const existingHoldingClass = holdingClassInvestmentTotals?.find(
            (hc) => hc.holdingClassId === holdingClass.holdingClassId,
          );

          return (
            existingHoldingClass &&
            existingHoldingClass.latestUnitPrice !== holdingClass.unitPriceTcy
          );
        });

  const holdingClassHasInvestments = holdingClassInvestmentTotals.some((hc) =>
    holdingClasses.map((hc) => hc.holdingClassId).includes(hc.holdingClassId),
  );

  const investmentHasOneHoldingClass = holdingClasses.length === 1;
  const revaluationText =
    'This share price is different from other shares you hold in this company. To avoid creating two valuations for the same share class and needing to revalue it later, you can process an automatic revaluation below.';

  const additionalRevaluationText =
    'As this investment has a different price for an existing share class, this share class will have two valuations in the system, so will require revaluation later if you do not revalue it here.';

  const options = [
    ...(investmentHasOneHoldingClass
      ? [
          {
            label: 'Revalue all existing shares with this share price and date',
            value: InvestmentRevaluationOption.AllShares,
          },
        ]
      : []),
    ...(holdingClassHasInvestments
      ? [
          {
            label:
              'Revalue this share class only with this share price and date ',
            value: InvestmentRevaluationOption.ShareClassOnly,
          },
        ]
      : []),
    {
      label: 'Do not revalue',
      value: InvestmentRevaluationOption.DoNotRevalue,
    },
    {
      label: 'Manually input share price and date',
      value: InvestmentRevaluationOption.ManuallyInput,
    },
  ];

  return (
    <RevaluationFormWrapper
      newRevaluations={newRevaluations}
      onPreviousStep={onCancel}
      onSaveDraft={onSuccess}
      onSaveAndApprove={onSuccess}
    >
      <Column spacing="lg">
        <Column spacing="xs">
          <Typography variant="h5">Revalue existing shares</Typography>
          <Typography variant="body2">
            {revaluationText}{' '}
            {!!unitPriceDifferentForHoldingClasses && additionalRevaluationText}
          </Typography>
        </Column>
        <Divider />
        <FormField label="Choose revaluation option*" size="lg">
          <Controller
            control={control}
            name="revaluationOption"
            render={({ field, fieldState }) => (
              <BasicSelect
                fullWidth
                {...field}
                options={options}
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </FormField>
      </Column>
    </RevaluationFormWrapper>
  );
};

export default RevalueInvestments;
